import { BlockAPI, BlockDefinition, BlockInstance } from "@softr-io/blocks-sdk";
import React, { FC, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { BlockProps, ChildBlockProps } from "@softr-io/blocks-sdk/react";
import { Manifest } from "../manifest";
import { useManifest } from "../useManifest";
import { fetchComponentScript } from "../fetchComponentScript";
import { usePageContext } from "../PageContext";
import { dispatchReactBlockReadyEvent, dispatchReactBlockUnmountEvent } from "../events";
import { BlockLoader } from "../BlockLoader";
import { PARENT_SELECTOR_ATTRIBUTE_NAME } from "@/parent-selector";
import { appendScopeToStyle } from "@/appendScopeToStyle";
import { getHeadStylesSentinel } from "@/headStylesSentinel";
import { normalizePath } from "@/block-wrapper/utils";
import { AppConstants, useAppConstants } from "@/AppConstantsContext";
import { fetchManifestV2Block } from "@/fetchManifestV2Block";
import DefinitionDevTool from "@/definition-dev-tool/DefinitionDevTool";
import { onBlockComponentLoaded, onBlockLoadError } from "@/useBlockReadiness";
import { BlockAPIReadyEvent } from "@/BlockAPIReadyEvent";

type Props = Readonly<{
    definition: BlockDefinition;
    topLevel: boolean;
    childBlocks: Readonly<Record<BlockDefinition["id"], BlockDefinition>> | undefined;
    childBlockRootProps: ChildBlockProps["wrapperProps"] | null;
    topbarRoot: HTMLElement | null;
    sidebarRoot: HTMLElement | null;
    bottombarRoot: HTMLElement | null;
}>;

export function React18Block({ definition, childBlocks, topbarRoot, bottombarRoot, sidebarRoot }: Props) {
    const {
        datasourceResourceIdentifiers,
        setBlockInstanceRef,
        theme,
        pageId,
        firstDetailsBlockHrid,
        currentRecord,
        currentRecordDefinition,
        blockIdToHrid,
        blockRecordIdMap,
        desktopNavLayout,
    } = usePageContext();
    const { baseUrl, slotIdAttributeName, slotBlockIdAttributeName, mode, appId, studioEventEmitter, spaMode } =
        useAppConstants();
    const [runtimeDefinition, setRuntimeDefinition] = useState(definition);
    const blockDefinition = shouldUseRuntimeDefinition(mode) ? runtimeDefinition : definition;
    const { name, version } = blockDefinition;
    const manifest = useManifest({ name, version, baseUrl });
    const isNavBlock = blockDefinition.name === "navigation";

    // The navigation block expects the `topbar-root`, `sidebar-root`, and `bottombar-root`
    // to be ready before its module is loaded.
    // We use this state to block the loading of the nav block bundle until it is safe to do so.
    const areNavRootsReady = Boolean(topbarRoot && sidebarRoot && bottombarRoot);

    const [Component, styles] = useBlockComponent({
        manifest,
        definition: blockDefinition,
        baseUrl,
        areNavRootsReady,
    });

    const ref = useCallback(
        (blockInstance: BlockInstance | null) => {
            setBlockInstanceRef(blockDefinition.id, blockInstance);
        },
        [blockDefinition.id, setBlockInstanceRef],
    );

    useImperativeHandle(ref, () => ({ api: Component?.api }));

    useLayoutEffect(() => {
        if (!Component) return;
        // The first time we run the layout after the Component is loaded,
        // we can notify the main PageRenderer, so that it can
        // wait for these events and resume the custom code execution.
        dispatchReactBlockReadyEvent({ blockId: blockDefinition.id });

        return () => {
            dispatchReactBlockUnmountEvent({ blockId: blockDefinition.id });
        };
    }, [Component, blockDefinition.id]);

    // Apply a unique attribute to the block root so that the
    // blocks can apply styles to the block root itself,
    // rather than the nodes that `Component` renders.
    // For example, check out the header block, which uses
    // useHeadStyle with parentSelector value to make the
    // header sticky (since the outer div isn't rendered by the component)
    // The query selector should match exactly 1 DOM element, which
    // is the block root for *this* block. It should not match
    // other instances of the same block name/version.
    const parentSelector = `[${PARENT_SELECTOR_ATTRIBUTE_NAME}="${blockDefinition.id}"]`;

    const scopedStyles = useMemo(
        () => styles.map((s) => appendScopeToStyle(s, parentSelector)),
        [styles, parentSelector],
    );

    useLayoutEffect(() => {
        /**
         * The reason why we're appending the styles to the document head instead of
         * rendering them inline here is because v4 blocks depend on @emotion/css,
         * which appends stylesheets to the document head.
         * The stylesheets must be in the correct order or the styles won't be applied correctly.
         *
         * In particular, the issue can be observed by doing the following
         * 1. Change document.head.appendChild(elem) to document.body.appendChild(elem) below
         * 2. Add a v4 form block
         * 3. Set the label color for a specific field (using the paintbrush icon when you hover over
         *    a field in the Features tab of the form block)
         * 4. The label color will not change.
         * This will work when the style is appended to document head instead.
         */
        const elems: HTMLStyleElement[] = [];
        const headStylesSentinel = getHeadStylesSentinel();
        for (const style of scopedStyles) {
            const elem = document.createElement("style");
            elem.setAttribute("data-trace", "React18Block.tsx");
            elem.innerHTML = style;

            headStylesSentinel.before(elem);
            elems.push(elem);
        }
        return () => elems.forEach((elem) => elem.remove());
    }, [scopedStyles]);

    const showLoader = !Component && mode === "studio";
    const scopeClassName = manifest?.version === "2" ? manifest.cssScopeClassName : "";

    return (
        <>
            {shouldUseRuntimeDefinition(mode) ? (
                <div style={{ position: "relative" }}>
                    <DefinitionDevTool
                        originalDefinition={definition}
                        definition={runtimeDefinition}
                        set={setRuntimeDefinition}
                    />
                </div>
            ) : null}

            {showLoader && <BlockLoader />}
            {Component && (
                <Component
                    {...blockDefinition}
                    pageId={pageId}
                    appId={appId}
                    definition={blockDefinition}
                    defaultTheme={theme}
                    parentSelector={parentSelector}
                    blocks={childBlocks}
                    studioEventEmitter={studioEventEmitter}
                    slotBlockIdAttributeName={slotBlockIdAttributeName ?? undefined}
                    slotIdAttributeName={slotIdAttributeName ?? undefined}
                    datasourceResourceIdentifiers={datasourceResourceIdentifiers}
                    spaMode={spaMode}
                    firstDetailsBlockHrid={firstDetailsBlockHrid}
                    blockIdToHrid={blockIdToHrid}
                    blockRecordIdMap={blockRecordIdMap}
                    topbarRoot={isNavBlock ? topbarRoot : null}
                    bottombarRoot={isNavBlock ? bottombarRoot : null}
                    sidebarRoot={isNavBlock ? sidebarRoot : null}
                    currentRecord={currentRecord}
                    currentRecordDefinition={currentRecordDefinition}
                    desktopNavLayout={desktopNavLayout}
                    scopeClassName={scopeClassName}
                />
            )}
        </>
    );
}

React18Block.displayName = "React18Block";

type UseBlockComponentProps = {
    definition: BlockDefinition;
    baseUrl: string;
    manifest: Manifest | null;
    areNavRootsReady: boolean;
};

type BlockComponent = FC<BlockProps> & { api?: BlockAPI };

function useBlockComponent({ definition, baseUrl, manifest, areNavRootsReady }: UseBlockComponentProps) {
    const [{ Component, styles }, setState] = useState<{ Component: BlockComponent | null; styles: string[] }>({
        Component: null,
        styles: [],
    });
    const { name, version } = definition;

    // The navigation block bundle can't be imported until `topbar-root`, `sidebar-root`, and
    // `bottombar-root` are in the DOM.
    // This is because the navigation block checks for these roots in its global scope.
    const waitingForNavRoots = name === "navigation" && !areNavRootsReady;
    useEffect(() => {
        const abortController = new AbortController();
        const { signal } = abortController;

        async function loadDependencies() {
            // It's not safe to load the block's component script if the block root is not ready yet
            // because the nav block checks for the navigation roots in its global scope.
            if (waitingForNavRoots) {
                return;
            }
            if (!manifest) {
                return;
            }
            // We're deliberately *not* passing in abort signals here.
            //
            // This is because fetchComponent internally caches the
            // Promise for each name/version pair. This means that various
            // blocks might be awaiting on the same underlying promise, making it
            // effectively non cancellable.
            // Instead, we have to check signal.aborted after each await point.
            // See https://softr.atlassian.net/browse/SE-7131
            let result: Awaited<ReturnType<typeof fetchComponentAndStyles>>;
            try {
                result = await fetchComponentAndStyles(manifest, { name, baseUrl, version });
                onBlockComponentLoaded({ name, version });
            } catch (error) {
                onBlockLoadError({ name, version });
                throw error;
            }
            if (signal.aborted) return;
            const [Component, styles] = result;
            setState({ Component, styles });
        }

        loadDependencies();
        return () => abortController.abort();
    }, [name, version, baseUrl, manifest, waitingForNavRoots]);
    return [Component, styles] as const;
}

async function fetchComponentAndStyles(
    manifest: Manifest,
    { name, version, baseUrl }: { name: string; version: string; baseUrl: string },
): Promise<[BlockComponent, string[]]> {
    if (manifest.version === "2") {
        const Component = await fetchManifestV2Block({
            blockName: name,
            blockVersion: version,
            baseUrl,
            manifest,
        });
        return [Component, []];
    } else {
        const urls = manifest.styles.map((style) => normalizePath(style, `${baseUrl}/${name}/${version}`));
        return Promise.all([
            fetchVarComponent({
                name,
                version,
                baseUrl,
                scriptName: manifest.scripts.main,
            }),
            Promise.all(urls.map((url) => fetch(url).then((it) => it.text()))),
        ]);
    }
}

type FetchVarComponentArgs = {
    name: string;
    version: string;
    baseUrl: string;
    scriptName: string;
};

/**
 * Blocks are expected to be built with webpack and expose a single global named renderBlock
 * The script for the block are fetched from the URL
 * : baseUrl/name/version/main.js
 * Example: https://localhost:5001/softr-blocks/dev/blocks/list-details8/4.1.0/main.js
 */
async function fetchVarComponent({ name, version, baseUrl, scriptName }: FetchVarComponentArgs) {
    const key = `${baseUrl}/${name}/${version}/${scriptName}`;
    const existing = fetchComponentCache.get(key);
    if (existing) {
        return existing;
    }
    const promise = _fetchVarComponent({ name, version, baseUrl, scriptName });
    fetchComponentCache.set(key, promise);
    return promise;
}

// TODO: Instead of a global cache, we should get a cache from the context.
//       This way, test setup doesn't have to clear this manually.
export const fetchComponentCache = new Map<string, Promise<FC<BlockProps>>>();

async function _fetchVarComponent({ name, baseUrl, version, scriptName }: FetchVarComponentArgs) {
    const scriptText = await fetchComponentScript({ baseUrl, name, version, scriptName });
    const scriptTag = document.createElement("script");
    // attributes for debugging. Should not be queried in any other way
    scriptTag.dataset.forBlockName = name;
    scriptTag.dataset.forBlockVersion = version;
    window.react18 = React;
    window.react_dom18 = ReactDOM;
    const globalComponentName = `SoftrBlock_${name}_${version}`;
    const wrappedScript = `
        {
            // React 18 blocks expect react and react-dom to be available
            // as variables with this name
            var react18 = window.react18;
            var react_dom18 = window.react_dom18;
            ${scriptText};
            if (typeof renderBlock !== "function") {
                throw new Error("No renderBlock function found", ${JSON.stringify({ name, version })});
            }
            // webpack builds expose a single global named renderBlock
            window["${globalComponentName}"] = renderBlock;
        }
    `;
    // in vitest, when we append a script tag to the document
    // globals aren't passed to the script, so the component
    // fails to find window.react18.
    // So, for test environment, we eval the script instead of appending it.
    // For the dev and prod, it's a bit nicer to be able to see the script in the DOM
    // for debuggability.
    if (process.env.NODE_ENV === "test") {
        eval(wrappedScript);
    } else {
        scriptTag.innerHTML = wrappedScript;
        document.body.appendChild(scriptTag);
    }

    const Component = (window as unknown as Record<string, unknown>)[globalComponentName] as BlockComponent;
    if (typeof Component !== "function") {
        throw new Error(`Expected window object to have a function at key ${globalComponentName}; Found ${Component}`);
    }
    BlockAPIReadyEvent.dispatch({ blockName: name, blockVersion: version, api: Component.api });
    return Component;
}

const runtimeDefinitionToolDisabled = localStorage.getItem("disableRuntimeDefinitionTool") === "true";
function shouldUseRuntimeDefinition(mode: AppConstants["mode"]) {
    return process.env.NODE_ENV === "development" && mode !== "studio" && !runtimeDefinitionToolDisabled;
}

declare global {
    interface Window {
        react18: typeof import("react");
        react_dom18: typeof import("react-dom");
    }
}
